// web/src/components/EmptyState.tsx
// Empty-state pattern. Big emoji as visual centrepiece (cheap, vivid,
// renders everywhere), headline, body, optional CTA. Used for:
//  - No kids on the account → ⚽
//  - No matches yet → 📓
//  - No photo on a match → 📷
//  - No achievements unlocked → 🏆
//  - Stats with insufficient data → 📊
//
// The emoji is decorative; aria-hidden is applied.

import type { ReactNode, CSSProperties } from 'react';

export interface EmptyStateProps {
  /** Decorative emoji (single character — rendered large). */
  emoji: string;
  title: ReactNode;
  body?: ReactNode;
  cta?: ReactNode;
  /** Visual size of the emoji centrepiece. */
  size?: 'sm' | 'md' | 'lg';
  style?: CSSProperties;
}

const EMOJI_SIZE: Record<NonNullable<EmptyStateProps['size']>, string> = {
  sm: '2.5rem',
  md: '4rem',
  lg: '6rem',
};

export function EmptyState({
  emoji,
  title,
  body,
  cta,
  size = 'md',
  style,
}: EmptyStateProps) {
  return (
    <div
      style={{
        textAlign: 'center',
        padding: 'var(--space-xl) var(--space-lg)',
        background: 'var(--surface-2)',
        border: '1px dashed var(--divider)',
        borderRadius: 'var(--radius-lg)',
        ...style,
      }}
    >
      <div
        aria-hidden
        style={{
          fontSize: EMOJI_SIZE[size],
          lineHeight: 1,
          marginBottom: 'var(--space-base)',
          // Small lift so the emoji looks placed, not floating
          filter: 'drop-shadow(0 4px 6px rgba(20,42,30,0.12))',
        }}
      >
        {emoji}
      </div>
      <h3
        style={{
          fontFamily: 'var(--font-display)',
          fontSize: 'var(--title)',
          color: 'var(--green)',
          margin: 0,
        }}
      >
        {title}
      </h3>
      {body ? (
        <p
          style={{
            margin: 'var(--space-sm) 0 var(--space-lg)',
            color: 'var(--muted)',
            fontSize: 'var(--small)',
            maxWidth: 360,
            marginLeft: 'auto',
            marginRight: 'auto',
            lineHeight: 1.5,
          }}
        >
          {body}
        </p>
      ) : null}
      {cta ? <div>{cta}</div> : null}
    </div>
  );
}
